import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { ChevronDown, CircleCheck } from 'lucide-react';
import { useId, useState } from 'react';
import { pluralise } from '@/shared/lib/format';
import type { AgentStep } from '../api/schemas';
import { AgentSteps } from './AgentSteps';
import { ElapsedSeconds } from './ElapsedSeconds';
import { shimmerTextSx } from './motion';

type Props = { steps: AgentStep[]; isRunning: boolean; startedAt: number; latencyMs?: number | undefined };

/** "Researching" with every step while the assistant works; one line, opened on demand, once the answer is in. */
export function StepsDisclosure({ steps, isRunning, startedAt, latencyMs }: Props) {
  const [isOpen, setOpen] = useState(false);
  const panelId = useId();

  if (isRunning) {
    return (
      <Stack sx={{ gap: 1.25 }}>
        <Stack direction="row" sx={{ alignItems: 'baseline', gap: 0.75 }}>
          <Typography variant="body2" sx={[{ fontWeight: 600, fontSize: '0.8125rem' }, shimmerTextSx]}>Researching</Typography>
          <Typography variant="caption" sx={{ color: 'text.disabled' }} aria-hidden>
            <ElapsedSeconds since={startedAt} />
          </Typography>
        </Stack>
        <AgentSteps steps={steps} showPlanned />
      </Stack>
    );
  }
  if (steps.length === 0) return null;

  const done = steps.filter((s) => s.status === 'done').length;
  const took = latencyMs ? ` · ${(latencyMs / 1000).toFixed(1)}s` : '';
  return (
    <div>
      <ButtonBase onClick={() => { setOpen((v) => !v); }} aria-expanded={isOpen} aria-controls={panelId} sx={toggleSx}>
        <CircleCheck size={13} aria-hidden />
        <Typography variant="caption" sx={{ fontWeight: 500 }}>Researched in {pluralise(done, 'step')}{took}</Typography>
        <Box component="span" sx={{ display: 'flex', transition: 'transform 150ms', transform: isOpen ? 'rotate(180deg)' : 'none' }}>
          <ChevronDown size={14} aria-hidden />
        </Box>
      </ButtonBase>
      <Collapse in={isOpen} id={panelId}>
        <Box sx={{ pt: 1, pb: 0.5 }}>
          <AgentSteps steps={steps} />
        </Box>
      </Collapse>
    </div>
  );
}

const toggleSx = { gap: 0.75, borderRadius: 1, px: 0.5, ml: -0.5, color: 'text.secondary', '&:hover': { color: 'text.primary' } } as const;
